/**
 * Admin Analytics Section - KPIs & Reports with Service Selection
 * Flow: Select Service (Build/Protect/Grow/Academy) → View Service-Specific Analytics
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/admin/AdminLayout';
import { 
  Target, Activity, Users, TrendingUp, DollarSign, BarChart3,
  ArrowUpRight, ArrowDownRight, FileText, Award, Percent, Clock,
  Loader2, Star, Zap, PieChart, ArrowLeft, Shield, Layers, Truck,
  GraduationCap
} from 'lucide-react';

const AdminAnalyticsSection = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState(null);
  const [activeTab, setActiveTab] = useState('kpis');
  
  // Service definitions for Analytics
  const services = {
    build: {
      name: 'Build',
      subtitle: 'Carrier Onboarding Analytics',
      icon: Layers,
      color: '#8B5CF6',
      description: 'Onboarding pipeline performance & conversion',
      stats: {
        totalClients: 47,
        conversionRate: 68,
        avgCompletionDays: 14,
        revenuePerClient: 1827,
        monthlyGrowth: 15.2,
        customerSatisfaction: 4.7
      },
      kpis: [
        { name: 'Conversion Rate', value: '68%', change: '+5.2%', up: true },
        { name: 'Avg Completion Time', value: '14 days', change: '-2 days', up: true },
        { name: 'Revenue/Client', value: '$1,827', change: '+$180', up: true },
        { name: 'Abandonment Rate', value: '12%', change: '-3%', up: true }
      ],
      topPerformers: [
        { name: 'Elite Package', value: '$89,910', metric: 'revenue' },
        { name: 'Accelerator Package', value: '19 clients', metric: 'volume' },
        { name: 'Foundation Package', value: '12 days', metric: 'fastest' }
      ]
    },
    protect: {
      name: 'Protect',
      subtitle: 'Compliance Analytics',
      icon: Shield,
      color: '#10B981',
      description: 'Compliance rates & retention metrics',
      stats: {
        totalClients: 89,
        complianceRate: 91,
        avgResponseTime: 24,
        retentionRate: 94,
        monthlyGrowth: 8.5,
        customerSatisfaction: 4.8
      },
      kpis: [
        { name: 'Compliance Rate', value: '91%', change: '+3%', up: true },
        { name: 'Client Retention', value: '94%', change: '+2%', up: true },
        { name: 'Avg Response Time', value: '24 hrs', change: '-6 hrs', up: true },
        { name: 'Churn Rate', value: '3.2%', change: '-0.8%', up: true }
      ],
      topPerformers: [
        { name: 'Enterprise Package', value: '98%', metric: 'compliance' },
        { name: 'Professional Package', value: '41 clients', metric: 'volume' },
        { name: 'Basic Package', value: '82%', metric: 'retention' }
      ]
    },
    grow: {
      name: 'Grow',
      subtitle: 'Dispatch Analytics',
      icon: Truck,
      color: '#F59E0B',
      description: 'Load performance & carrier growth',
      stats: {
        totalClients: 77,
        avgRatePerMile: 2.78,
        totalLoads: 1974,
        retentionRate: 88,
        monthlyGrowth: 12.3,
        customerSatisfaction: 4.6
      },
      kpis: [
        { name: 'Avg Rate/Mile', value: '$2.78', change: '+$0.15', up: true },
        { name: 'Total Loads', value: '1,974', change: '+234', up: true },
        { name: 'Client Retention', value: '88%', change: '+4%', up: true },
        { name: 'Deadhead %', value: '8.2%', change: '-1.5%', up: true }
      ],
      topPerformers: [
        { name: 'Gold Package', value: '$3.12', metric: 'rate/mile' },
        { name: 'Silver Package', value: '28 clients', metric: 'volume' },
        { name: 'Bronze Package', value: '78%', metric: 'retention' }
      ]
    },
    academy: {
      name: 'Academy',
      subtitle: 'Learning Analytics',
      icon: GraduationCap,
      color: '#D4AF37',
      description: 'Course completion & engagement metrics',
      stats: {
        totalStudents: 85,
        completionRate: 72,
        avgEngagement: 68,
        coursesActive: 12,
        monthlyGrowth: 18.5,
        customerSatisfaction: 4.9
      },
      kpis: [
        { name: 'Completion Rate', value: '72%', change: '+8%', up: true },
        { name: 'Avg Engagement', value: '68%', change: '+12%', up: true },
        { name: 'Course Rating', value: '4.9', change: '+0.2', up: true },
        { name: 'Dropout Rate', value: '15%', change: '-5%', up: true }
      ],
      topPerformers: [
        { name: 'Authority 101', value: '156 enrolled', metric: 'popular' },
        { name: 'Compliance Basics', value: '89%', metric: 'completion' },
        { name: 'Dispatch Mastery', value: '4.9★', metric: 'rated' }
      ]
    }
  };

  // Overall stats
  const overallStats = {
    totalClients: 298,
    platformRevenue: 158101,
    avgSatisfaction: 4.75,
    overallGrowth: 13.6
  };

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    setTimeout(() => setLoading(false), 500);
  }, [navigate]);

  if (loading) {
    return (
      <AdminLayout title="Analytics" subtitle="Loading...">
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-8 h-8 text-[#3B82F6] animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  // Service Selection Screen
  if (!selectedService) {
    return (
      <AdminLayout 
        title="Analytics Center" 
        subtitle="KPIs & Reports - Select a service to view analytics"
      >
        {/* Overall Summary */}
        <div className="bg-gradient-to-r from-[#3B82F6]/20 to-[#3B82F6]/5 border border-[#3B82F6]/30 p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-white text-lg font-bold mb-1">Platform Analytics</h2>
              <p className="text-white/50 text-sm">Performance metrics across all services</p>
            </div>
            <div className="flex items-center gap-4 sm:gap-6">
              <div className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-white">{overallStats.totalClients}</p>
                <p className="text-white/40 text-xs">Total Clients</p>
              </div>
              <div className="h-10 w-px bg-white/10" />
              <div className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-[#22C55E]">${(overallStats.platformRevenue / 1000).toFixed(0)}k</p>
                <p className="text-white/40 text-xs">Platform Revenue</p>
              </div>
              <div className="h-10 w-px bg-white/10" />
              <div className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-[#D4AF37]">{overallStats.avgSatisfaction}★</p>
                <p className="text-white/40 text-xs">Avg Satisfaction</p>
              </div>
              <div className="h-10 w-px bg-white/10" />
              <div className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-[#10B981]">+{overallStats.overallGrowth}%</p>
                <p className="text-white/40 text-xs">Growth</p>
              </div>
            </div>
          </div>
        </div>

        {/* Service Selection Cards */}
        <h3 className="text-white font-bold mb-4">Select a Service to View Analytics</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.entries(services).map(([key, service]) => (
            <div
              key={key}
              onClick={() => setSelectedService(key)}
              className="bg-[#121212] border border-white/10 p-6 cursor-pointer hover:bg-[#1a1a1a] hover:border-white/20 transition-all"
              data-testid={`analytics-service-${key}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 rounded" style={{ backgroundColor: `${service.color}20` }}>
                  <service.icon className="w-6 h-6" style={{ color: service.color }} />
                </div>
                <div>
                  <p className="text-white font-bold">{service.name}</p>
                  <p className="text-white/50 text-xs">{service.subtitle}</p>
                </div>
              </div>

              <p className="text-white/50 text-sm mb-4">{service.description}</p>

              <div className="border-t border-white/10 pt-4 mt-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-white/40 text-xs">Clients</span>
                  <span className="text-white font-bold">{service.stats.totalClients}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/40 text-xs">Growth</span>
                  <span className="text-[#10B981] font-bold">+{service.stats.monthlyGrowth}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/40 text-xs">Satisfaction</span>
                  <span className="text-[#D4AF37] font-bold">{service.stats.customerSatisfaction}★</span>
                </div>
              </div>

              <button 
                className="w-full mt-4 py-3 text-sm font-medium transition-all"
                style={{ 
                  backgroundColor: `${service.color}20`, 
                  color: service.color,
                  border: `1px solid ${service.color}40`
                }}
              >
                View {service.name} Analytics →
              </button>
            </div>
          ))}
        </div>
      </AdminLayout>
    );
  }

  // Service-Specific Analytics Screen
  const service = services[selectedService];

  return (
    <AdminLayout 
      title={`${service.name} Analytics`} 
      subtitle={service.subtitle}
    >
      {/* Back Button */}
      <button
        onClick={() => setSelectedService(null)}
        className="flex items-center gap-2 text-white/50 hover:text-white mb-6 transition-colors"
        data-testid="analytics-back-button"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to All Services
      </button>

      {/* Service Header */}
      <div className="border p-4 sm:p-6 mb-6" style={{ 
        backgroundColor: `${service.color}10`, 
        borderColor: `${service.color}40` 
      }}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded" style={{ backgroundColor: `${service.color}20` }}>
              <service.icon className="w-8 h-8" style={{ color: service.color }} />
            </div>
            <div>
              <h2 className="text-white text-xl font-bold">{service.name} - {service.subtitle}</h2>
              <p className="text-white/50">{service.stats.totalClients} clients tracked</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-[#10B981]">+{service.stats.monthlyGrowth}%</p>
              <p className="text-white/40 text-xs">Monthly Growth</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-[#D4AF37]">{service.stats.customerSatisfaction}★</p>
              <p className="text-white/40 text-xs">Satisfaction</p>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        {service.kpis.map((kpi, idx) => (
          <div key={idx} className="bg-[#121212] border border-white/10 p-4 sm:p-5">
            <p className="text-white/50 text-sm mb-2">{kpi.name}</p>
            <p className="text-2xl font-bold text-white">{kpi.value}</p>
            <p className={`text-sm mt-1 flex items-center gap-1 ${kpi.up ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
              {kpi.up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              {kpi.change}
            </p>
          </div>
        ))}
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 mb-6 border-b border-white/10 pb-4 overflow-x-auto">
        {[
          { id: 'kpis', label: 'KPI Dashboard', icon: Target },
          { id: 'performance', label: 'Performance', icon: Activity },
          { id: 'reports', label: 'Reports', icon: FileText },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`analytics-tab-${tab.id}`}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === tab.id
                ? 'bg-[#3B82F6]/10 border border-[#3B82F6]/30'
                : 'text-white/50 hover:text-white border border-transparent'
            }`}
            style={{ color: activeTab === tab.id ? service.color : undefined }}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* KPIs Tab */}
      {activeTab === 'kpis' && (
        <div className="space-y-6">
          <div className="bg-[#121212] border border-white/10 overflow-hidden">
            <div className="p-4 border-b border-white/10">
              <h3 className="text-white font-bold">Key Performance Indicators</h3>
            </div>
            <div className="divide-y divide-white/5">
              {service.kpis.map((kpi, idx) => (
                <div key={idx} className="p-4 flex items-center justify-between hover:bg-white/5">
                  <span className="text-white">{kpi.name}</span>
                  <div className="flex items-center gap-4">
                    <span className="text-white font-bold">{kpi.value}</span>
                    <span className={`flex items-center gap-1 text-sm ${kpi.up ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                      {kpi.up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                      {kpi.change}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Performers */}
          <div className="bg-[#121212] border border-white/10 p-6">
            <h3 className="text-white font-bold mb-4">Top Performers</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {service.topPerformers.map((performer, idx) => (
                <div key={idx} className="bg-[#0A0A0A] border border-white/10 p-4 text-center">
                  <Award className="w-8 h-8 mx-auto mb-2" style={{ color: service.color }} />
                  <p className="text-white font-medium">{performer.name}</p>
                  <p className="text-xl font-bold" style={{ color: service.color }}>{performer.value}</p>
                  <p className="text-white/40 text-xs">{performer.metric}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Performance Tab */}
      {activeTab === 'performance' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-[#121212] border border-white/10 p-6 text-center">
              <Users className="w-12 h-12 mx-auto mb-3" style={{ color: service.color }} />
              <p className="text-4xl font-bold text-white">{service.stats.totalClients}</p>
              <p className="text-white/50">Total Clients</p>
            </div>
            <div className="bg-[#121212] border border-white/10 p-6 text-center">
              <TrendingUp className="w-12 h-12 text-[#10B981] mx-auto mb-3" />
              <p className="text-4xl font-bold text-[#10B981]">+{service.stats.monthlyGrowth}%</p>
              <p className="text-white/50">Monthly Growth</p>
            </div>
            <div className="bg-[#121212] border border-white/10 p-6 text-center">
              <Star className="w-12 h-12 text-[#D4AF37] mx-auto mb-3" />
              <p className="text-4xl font-bold text-[#D4AF37]">{service.stats.customerSatisfaction}</p>
              <p className="text-white/50">Customer Satisfaction</p>
            </div>
          </div>
        </div>
      )}

      {/* Reports Tab */}
      {activeTab === 'reports' && (
        <div className="bg-[#121212] border border-white/10 p-6">
          <h3 className="text-white font-bold mb-4">{service.name} Reports</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { name: `${service.name} Weekly Performance`, type: 'PDF', date: 'Mar 17, 2026' },
              { name: `${service.name} Monthly Summary`, type: 'PDF', date: 'Mar 1, 2026' },
              { name: `${service.name} Client Analytics`, type: 'CSV', date: 'Mar 15, 2026' },
              { name: `${service.name} KPI Snapshot`, type: 'PDF', date: 'Mar 19, 2026' }
            ].map((report, idx) => (
              <div key={idx} className="bg-[#0A0A0A] border border-white/10 p-4 flex items-center justify-between hover:border-white/20 cursor-pointer transition-colors">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5" style={{ color: service.color }} />
                  <div>
                    <p className="text-white font-medium">{report.name}</p>
                    <p className="text-white/40 text-xs">{report.type} • {report.date}</p>
                  </div>
                </div>
                <button className="text-sm hover:underline" style={{ color: service.color }}>Download</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminAnalyticsSection;
